import {
  CheckboxField,
  CheckboxSize,
  CheckboxType,
  CheckboxVariant,
  Color,
  FontWeight,
  formatCurrency,
  Icon,
  IconName,
  Slider,
  Tooltip,
  Typography,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { PageDataContext } from '../../components/page';
import { PageData } from '../../interfaces';
import { CenteredContentSlideWrapper } from '../centeredContentSlideWrapper/centeredContentSlideWrapper.component';

import { EventType } from './enums';
import {
  BrandColorTypography,
  ExerciseStep,
  ExerciseStepTitle,
  IconWrapper,
  InnerWrapper,
  RadioButtons,
  StyledMarkdown,
  StyledRadioButtons,
  TextsWithTooltipWrapper,
  TextWithTooltip,
  TooltipBase,
} from './insuranceValueCalculator.styled';
import { InsuranceValueCalculatorProps } from './insuranceValueCalculator.types';

export const InsuranceValueCalculator: React.FC<InsuranceValueCalculatorProps> = (
  props: InsuranceValueCalculatorProps
): JSX.Element => {
  const {
    baseUrl,
    centeredContentSlideWrapperData,
    callComplete,
    completed,
  }: InsuranceValueCalculatorProps = props;
  const { t }: TransProps<never> = useTranslation();
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const {
    slidesOverallCounter,
    currentSlideCounter,
    title,
    description,
  }: Partial<PageData> = dataCtx ?? {};
  const loanValue: number = 54000;
  const loanDurationMonths: number = 36;
  const borrowersNumberValues: number[] = [1, 2, 3, 4];
  const [borrowersNumber, setBorrowersNumber]: UseState<number> = useState<number>(1);
  const monthlyInstallmentValue: number = loanValue / loanDurationMonths;
  const monthlyCompensationValue: number = useMemo((): number => (
    monthlyInstallmentValue / borrowersNumber > 3000
      ? 3000
      : monthlyInstallmentValue / borrowersNumber
  ), [borrowersNumber, monthlyInstallmentValue]);
  const [event, setEvent]: UseState<EventType> = useState<EventType>(EventType.Death);
  const [eventMonth, setEventMonth]: UseState<number> = useState<number>(1);
  const [eventMonthToDisplay, setEventMonthToDisplay]: UseState<number> = useState<number>(1);
  const debtSaldo: number = useMemo((): number => (
    (loanDurationMonths - eventMonth + 1) * monthlyInstallmentValue
  ), [eventMonth, monthlyInstallmentValue]);
  const debtSaldoPerBorrower: number = useMemo((): number => debtSaldo / borrowersNumber, [debtSaldo, borrowersNumber]);
  const isFullLoanCompensation: boolean = useMemo((): boolean => (
    !![EventType.PermanentWorkIncapacity, EventType.Death].includes(event)
  ), [event]);

  const handleRadioButtonChange: (newEvent: EventType) => void = (newEvent: EventType): void => {
    if (event !== newEvent) {
      setEvent(newEvent);
    }
  };

  useEffect((): void => {
    if (!completed) {
      callComplete();
    }
  }, [completed, callComplete]);

  return (
    <CenteredContentSlideWrapper
      currentSlideCounter={currentSlideCounter}
      description={description}
      leftImageSrc={baseUrl + centeredContentSlideWrapperData.leftImageSrc}
      rightImageSrc={baseUrl + centeredContentSlideWrapperData.rightImageSrc}
      slidesOverallCounter={slidesOverallCounter}
      title={title}
    >
      <TextsWithTooltipWrapper>
        <TextWithTooltip>
          <Tooltip title={t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.loanValueTooltip')}>
            <TooltipBase>
              <Icon color={Color.Brand100} name={IconName.Info} size={24} />
              <BrandColorTypography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextLG}>
                {t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.loanValue')}
              </BrandColorTypography>
            </TooltipBase>
          </Tooltip>
          <Typography variant={TypographyVariant.TextLG}>
            {formatCurrency(loanValue, undefined, undefined, true)}
          </Typography>
        </TextWithTooltip>
        <TextWithTooltip>
          <Tooltip title={t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.loanDurationTooltip')}>
            <TooltipBase>
              <Icon color={Color.Brand100} name={IconName.Info} size={24} />
              <BrandColorTypography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextLG}>
                {t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.loanDurationLabel')}
              </BrandColorTypography>
            </TooltipBase>
          </Tooltip>
          <Typography variant={TypographyVariant.TextLG}>
            {t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.loanDurationValue')}
          </Typography>
        </TextWithTooltip>
        <TextWithTooltip>
          <Tooltip title={t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.monthlyInstallmentTooltip')}>
            <TooltipBase>
              <Icon color={Color.Brand100} name={IconName.Info} size={24} />
              <BrandColorTypography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextLG}>
                {t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.monthlyInstallment')}
              </BrandColorTypography>
            </TooltipBase>
          </Tooltip>
          <Typography variant={TypographyVariant.TextLG}>
            {formatCurrency(monthlyInstallmentValue, undefined, undefined, true)}
          </Typography>
        </TextWithTooltip>
      </TextsWithTooltipWrapper>
      <InnerWrapper>
        <ExerciseStep>
          <ExerciseStepTitle fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
            {t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.exerciseStepTitle.event')}
          </ExerciseStepTitle>
          <RadioButtons>
            <CheckboxField
              checked={event === EventType.Death}
              label={t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.eventCheckbox.death')}
              onChange={(): void => handleRadioButtonChange(EventType.Death)}
              size={CheckboxSize.Medium}
              type={CheckboxType.Radio}
              variant={CheckboxVariant.SimpleText}
            />
            <CheckboxField
              checked={event === EventType.PermanentWorkIncapacity}
              label={t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.eventCheckbox.workIncapacity')}
              onChange={(): void => handleRadioButtonChange(EventType.PermanentWorkIncapacity)}
              size={CheckboxSize.Medium}
              type={CheckboxType.Radio}
              variant={CheckboxVariant.SimpleText}
            />
            <CheckboxField
              checked={event === EventType.JobLoss}
              label={t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.eventCheckbox.jobLoss')}
              onChange={(): void => handleRadioButtonChange(EventType.JobLoss)}
              size={CheckboxSize.Medium}
              type={CheckboxType.Radio}
              variant={CheckboxVariant.SimpleText}
            />
            <CheckboxField
              checked={event === EventType.Hospitalization}
              label={t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.eventCheckbox.hospitalization')}
              onChange={(): void => handleRadioButtonChange(EventType.Hospitalization)}
              size={CheckboxSize.Medium}
              type={CheckboxType.Radio}
              variant={CheckboxVariant.SimpleText}
            />
          </RadioButtons>
        </ExerciseStep>
        <ExerciseStep>
          <ExerciseStepTitle fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
            <StyledMarkdown
              text={t(
                'competence.app.scrollLesson.componentsV2.insuranceValueCalculator.exerciseStepTitle.monthOfEvent',
                { month: eventMonthToDisplay }
              )}
            />
          </ExerciseStepTitle>
          <Slider
            maxValue={loanDurationMonths}
            onSliderRelease={setEventMonth}
            onValueChange={setEventMonthToDisplay}
          />
        </ExerciseStep>
        <ExerciseStep>
          <ExerciseStepTitle fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
            {t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.exerciseStepTitle.debtSaldo')}
          </ExerciseStepTitle>
          <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.DisplayMD}>
            {formatCurrency(debtSaldo, undefined, undefined, true)}
          </Typography>
        </ExerciseStep>
        <ExerciseStep>
          <ExerciseStepTitle fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
            {t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.exerciseStepTitle.borrowersNumber')}
          </ExerciseStepTitle>
          <StyledRadioButtons>
            {
              borrowersNumberValues.map((value: number): JSX.Element => (
                <CheckboxField
                  checked={borrowersNumber === value}
                  key={value}
                  label={t(
                    `competence.app.scrollLesson.componentsV2.insuranceValueCalculator.borrowersNumberCheckbox.${value === 1
                      ? 'single'
                      : 'multiple'
                    }`,
                    { borrowersNumber: value }
                  )}
                  onChange={(): void => setBorrowersNumber(value)}
                  size={CheckboxSize.Medium}
                  type={CheckboxType.Radio}
                  variant={CheckboxVariant.SimpleText}
                />
              ))
            }
          </StyledRadioButtons>
        </ExerciseStep>
        <ExerciseStep>
          <ExerciseStepTitle fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
            {t(`competence.app.scrollLesson.componentsV2.insuranceValueCalculator.exerciseStepTitle.${isFullLoanCompensation
              ? 'compensationValue'
              : 'monthlyInstallmentValue'
            }`)}
            {!isFullLoanCompensation && (
              <Tooltip title={t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.maxMonthlyInstallmentTooltip')}>
                <IconWrapper>
                  <Icon color={Color.Brand300} name={IconName.Info} size={24} />
                </IconWrapper>
              </Tooltip>
            )}
          </ExerciseStepTitle>
          <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.DisplayMD}>
            {formatCurrency(isFullLoanCompensation ? debtSaldoPerBorrower : monthlyCompensationValue, undefined, undefined, true)}
          </Typography>
        </ExerciseStep>
      </InnerWrapper>
      <Typography variant={TypographyVariant.Italic}>
        {t('competence.app.scrollLesson.componentsV2.insuranceValueCalculator.disclaimer')}
      </Typography>
    </CenteredContentSlideWrapper>
  );
};
